import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  Lock, TrendingUp, DollarSign, Building, MapPin, ArrowRight,
  Eye, ExternalLink, Shield,
  Calendar, CheckCircle, Briefcase
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const INVESTMENT_TYPES = [
  { value: "buy_hold", label: "Buy & Hold" },
  { value: "flip", label: "Fix & Flip" },
  { value: "value_add", label: "Value-Add" },
  { value: "turnkey", label: "Turnkey Rental" },
];

const BUDGET_RANGES = [
  { value: "under_100k", label: "Under $100K" },
  { value: "100k_250k", label: "$100K - $250K" },
  { value: "250k_500k", label: "$250K - $500K" },
  { value: "500k_1m", label: "$500K - $1M" },
  { value: "over_1m", label: "$1M+" },
];

const InvestorFunnelPage = () => {
  const { toast } = useToast();
  const [investorId, setInvestorId] = useState(() => localStorage.getItem("investor_lead_id") || "");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "",
    investment_type: "buy_hold", budget_range: "100k_250k"
  });

  const isUnlocked = !!investorId;

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const unlockToken = localStorage.getItem("investor_unlock_token") || "";
      const url = investorId
        ? `${API}/investor-deals?unlocked_by=${investorId}&unlock_token=${encodeURIComponent(unlockToken)}`
        : `${API}/investor-deals`;
      const res = await axios.get(url);
      setDeals(res.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [investorId]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/investor-lead`, formData);
      const leadId = res.data.lead_id;
      const unlockToken = res.data.unlock_token;
      localStorage.setItem("investor_lead_id", leadId);
      if (unlockToken) localStorage.setItem("investor_unlock_token", unlockToken);
      setInvestorId(leadId);
      toast({ title: "Access Granted", description: "Welcome to the private investor network. Check your email." });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Something went wrong.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const handleUnlock = async (dealId) => {
    try {
      const unlockToken = localStorage.getItem("investor_unlock_token") || "";
      await axios.post(
        `${API}/investor-deals/unlock/${dealId}?lead_id=${investorId}&unlock_token=${encodeURIComponent(unlockToken)}`
      );
      await fetchDeals();
      toast({ title: "Deal Unlocked", description: "Full details are now visible." });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.response?.data?.detail || "Failed to unlock deal.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#0A2342] py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="container-main text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 border border-violet-500/30 rounded-full text-violet-300 text-sm font-medium mb-6" data-testid="investor-badge">
              <Lock className="w-4 h-4" /> Private Investor Network
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Playfair_Display']" data-testid="investor-hero-title">
              Off-Market Investment Deals
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Exclusive access to distressed and underperforming Houston properties.
              Deals shared only with our vetted investor network.
            </p>
            {!isUnlocked && (
              <a href="#investor-form" className="inline-flex items-center gap-2 bg-violet-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-violet-700 transition-colors" data-testid="investor-cta-hero">
                Get Access <ArrowRight className="w-4 h-4" />
              </a>
            )}
            {isUnlocked && (
              <div className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 px-6 py-3 rounded-lg text-sm font-medium" data-testid="investor-access-status">
                <CheckCircle className="w-4 h-4" /> You have investor access
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 bg-slate-50">
        <div className="container-main">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Lock, title: "Exclusive Access", desc: "Properties from our network before they hit the open market" },
              { icon: TrendingUp, title: "Higher Returns", desc: "Less competition means better prices, terms, and cash flow" },
              { icon: Shield, title: "Vetted Deals", desc: "Every property is analyzed for strategy fit and upside potential" },
            ].map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="text-center p-6 bg-white rounded-xl border border-slate-200">
                <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-violet-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0A2342] mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture Form (shown if not unlocked) */}
      {!isUnlocked && (
        <section id="investor-form" className="py-16 bg-[#0A2342]">
          <div className="container-main">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 className="text-3xl font-bold text-white mb-6 font-['Playfair_Display']">
                  Join the Investor Network
                </h2>
                <p className="text-slate-300 mb-6">
                  Submit your details to unlock the off-market deal feed.
                  You'll also receive deal alerts via email and SMS when new opportunities match your profile.
                </p>
                <ul className="space-y-3 text-slate-300">
                  {["Instant access to off-market deals", "Personalized deal alerts (Email + SMS)", "Direct line to our investment team", "Strategy-matched opportunities"].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-violet-400 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8" data-testid="investor-form">
                  <div className="flex items-center gap-2 mb-6">
                    <Briefcase className="w-5 h-5 text-violet-600" />
                    <h3 className="text-xl font-bold text-[#0A2342]">Investor Application</h3>
                  </div>
                  <div className="space-y-4">
                    <input type="text" required placeholder="Full Name" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-field" data-testid="investor-form-name" />
                    <input type="email" required placeholder="Email" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-field" data-testid="investor-form-email" />
                    <input type="tel" required placeholder="Phone" value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-field" data-testid="investor-form-phone" />
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Investment Strategy</label>
                      <select value={formData.investment_type}
                        onChange={(e) => setFormData({ ...formData, investment_type: e.target.value })}
                        className="input-field" data-testid="investor-form-type">
                        {INVESTMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Budget Range</label>
                      <select value={formData.budget_range}
                        onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                        className="input-field" data-testid="investor-form-budget">
                        {BUDGET_RANGES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                      </select>
                    </div>
                    <button type="submit" disabled={submitting}
                      className="w-full py-3 bg-violet-600 text-white rounded-lg font-semibold hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
                      data-testid="investor-form-submit">
                      {submitting ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        : <><Lock className="w-4 h-4" /> Get Access</>}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Deal Feed */}
      <section className="py-16" data-testid="investor-deals-section">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
              {isUnlocked ? "Off-Market Deal Feed" : "Current Opportunities"}
            </h2>
            <p className="text-slate-600">
              {isUnlocked
                ? "Click any deal to unlock full details including address, financials, and notes."
                : "Submit your investor application above to unlock full deal details."}
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600" />
            </div>
          ) : deals.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="investor-deals-grid">
              {deals.map((deal, i) => (
                <InvestorDealCard
                  key={deal.id}
                  deal={deal}
                  index={i}
                  isLoggedIn={isUnlocked}
                  onUnlock={handleUnlock}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Building className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No off-market deals available right now. We'll alert you when new ones drop.</p>
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-50">
        <div className="container-main text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
            Ready to Talk Strategy?
          </h2>
          <p className="text-slate-600 mb-8 max-w-lg mx-auto">
            Schedule a free consultation with our investment team to discuss your goals and get matched to the right deals.
          </p>
          <a href="/book" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0A2342] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#0d2d54] transition-colors"
            data-testid="investor-schedule-call">
            <Calendar className="w-4 h-4" /> Schedule Investor Call
          </a>
        </div>
      </section>
    </div>
  );
};

/* ─── Investor Deal Card (gated view) ─── */
const InvestorDealCard = ({ deal, index, isLoggedIn, onUnlock }) => {
  const isLocked = !deal.unlocked && isLoggedIn;
  const isGated = !isLoggedIn;
  const strategy = (deal.investment_strategy || "").replace(/_/g, " ");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06, duration: 0.35 }}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all group"
      data-testid={`investor-deal-card-${index}`}
    >
      {/* Strategy strip */}
      {strategy && (
        <div className="px-4 py-2 bg-violet-600 flex items-center gap-1.5">
          <Briefcase className="w-3.5 h-3.5 text-white/80" />
          <span className="text-white text-xs font-medium capitalize">{strategy}</span>
          {deal.estimated_rent_upside > 0 && (
            <span className="ml-auto text-xs text-violet-200 font-semibold flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {deal.estimated_rent_upside}% upside
            </span>
          )}
        </div>
      )}

      <div className="p-5">
        {/* Header */}
        <h3 className="text-lg font-bold text-[#0A2342] mb-1 font-['Playfair_Display'] group-hover:text-violet-600 transition-colors line-clamp-1">
          {deal.property_name}
        </h3>

        {/* Location */}
        <div className="flex items-center text-slate-500 text-xs mb-3 gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          {deal.unlocked && deal.address ? (
            <span className="truncate">{deal.area} &mdash; {deal.address}</span>
          ) : (
            <span className="truncate">{deal.area} {(isLocked || isGated) && <span className="text-violet-500 font-medium">(Address hidden)</span>}</span>
          )}
        </div>

        {/* Details */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0A2342] text-white text-xs font-semibold">
            <DollarSign className="w-3 h-3" /> ${deal.base_rent?.toLocaleString()}/mo
          </span>
          {deal.bedrooms && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
              <Building className="w-3 h-3" /> {deal.bedrooms}
            </span>
          )}
          {deal.occupancy_opportunity && (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
              deal.occupancy_opportunity === "high" ? "bg-emerald-100 text-emerald-700" :
              deal.occupancy_opportunity === "medium" ? "bg-amber-100 text-amber-700" :
              "bg-red-100 text-red-700"
            }`}>
              {deal.occupancy_opportunity} opp.
            </span>
          )}
        </div>

        {/* Teaser */}
        {deal.investor_teaser && (
          <p className="text-sm text-slate-600 mb-4 italic line-clamp-2">"{deal.investor_teaser}"</p>
        )}

        {/* Unlocked details */}
        {deal.unlocked && (
          <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 mb-4 space-y-1 text-sm">
            {deal.address && <p className="text-slate-700"><span className="font-medium text-violet-700">Address:</span> {deal.address}</p>}
            {deal.investor_notes && <p className="text-slate-700"><span className="font-medium text-violet-700">Notes:</span> {deal.investor_notes}</p>}
            {deal.fees > 0 && <p className="text-slate-700"><span className="font-medium text-violet-700">Fees:</span> ${deal.fees}/mo</p>}
            {deal.bathrooms && <p className="text-slate-700"><span className="font-medium text-violet-700">Bath:</span> {deal.bathrooms}</p>}
            {deal.source_link && (
              <a href={deal.source_link} target="_blank" rel="noopener noreferrer"
                className="text-violet-600 hover:underline text-xs flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> View listing
              </a>
            )}
          </div>
        )}

        {/* CTA */}
        {isGated ? (
          <a href="#investor-form"
            className="w-full py-2.5 bg-slate-100 text-slate-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors"
            data-testid={`investor-deal-locked-${index}`}>
            <Lock className="w-3.5 h-3.5" /> Apply to Unlock Details
          </a>
        ) : deal.unlocked ? (
          <a href="/book" target="_blank" rel="noopener noreferrer"
            className="w-full py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
            data-testid={`investor-deal-call-${index}`}>
            <Calendar className="w-3.5 h-3.5" /> Discuss This Deal
          </a>
        ) : (
          <button onClick={() => onUnlock(deal.id)}
            className="w-full py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
            data-testid={`investor-deal-unlock-${index}`}>
            <Eye className="w-3.5 h-3.5" /> Unlock Full Details
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default InvestorFunnelPage;
